import type { InboxViewColumn, InboxViewRow } from "@/types/inboxView/inboxView";

const FALLBACK_CHIPS = [
  "Add a phone column",
  "Add a follow-up status column",
  "Group by the first column",
  "Drop the last column",
];

// Build refine suggestions that actually match this view's columns. We surface
// "only show where X = Y" for enum/boolean columns whose values we can observe
// in the current rows. Falls back to generic add/drop chips when we have no
// signal yet (e.g. before the first run completes).
export function deriveRefineChips(columns: InboxViewColumn[], rows: InboxViewRow[]): string[] {
  const chips: string[] = [];

  for (const col of columns) {
    if (chips.length >= 4) break;
    if (col.type === "boolean") {
      chips.push(`Only show where ${col.name} is yes`);
      continue;
    }
    if (col.type === "enum") {
      const counts = new Map<string, number>();
      for (const row of rows) {
        const v = row.data?.[col.name];
        if (typeof v === "string" && v.trim()) {
          counts.set(v, (counts.get(v) ?? 0) + 1);
        }
      }
      const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
      if (top) chips.push(`Only show where ${col.name} is ${top[0]}`);
    }
  }

  if (chips.length < 4 && columns.length > 0) {
    chips.push(`Drop the ${columns[columns.length - 1].name} column`);
  }

  if (chips.length === 0) return FALLBACK_CHIPS;
  // Pad with fallbacks if we still have space, skipping anything that overlaps.
  for (const fb of FALLBACK_CHIPS) {
    if (chips.length >= 4) break;
    if (!chips.includes(fb)) chips.push(fb);
  }
  return chips.slice(0, 4);
}
